/**
 * Descripción legible de un evento canónico, para el feed del vivo.
 * Puro: entra un `ScoutEvent`, sale una línea en el idioma activo.
 */
import type { ScoutEvent, TeamSide } from '@scout/core';
import { getT, type TFunction } from '../i18n/index';
import { playerLabel, reasonLabel, skillLabel } from './format';

export interface DescribeNames {
  home: string;
  away: string;
  /** camiseta → nombre, por lado (opcional). */
  players?: Record<TeamSide, Map<number, string | undefined>>;
}

function teamName(side: TeamSide, names: DescribeNames): string {
  return side === 'home' ? names.home : names.away;
}

function player(
  side: TeamSide,
  number: number | null,
  names: DescribeNames,
  t: TFunction,
): string {
  if (number === null) return t('describe.unknownPlayer');
  return playerLabel(number, names.players?.[side].get(number));
}

export function describeEvent(
  event: ScoutEvent,
  names: DescribeNames,
  t: TFunction = getT(),
): string {
  switch (event.type) {
    case 'match_created':
      return t('describe.matchCreated', { name: event.payload.name });
    case 'set_started':
      return t('describe.setStarted', { set: event.payload.setNumber });
    case 'set_ended':
      return t('describe.setEnded', {
        set: event.payload.setNumber,
        home: event.payload.homeScore,
        away: event.payload.awayScore,
        team: teamName(event.payload.wonBy, names),
      });
    case 'match_ended':
      return t('describe.matchEnded', {
        team: teamName(event.payload.wonBy, names),
        setsHome: event.payload.setsHome,
        setsAway: event.payload.setsAway,
      });
    case 'lineup_set':
      return t('describe.lineupSet', {
        team: teamName(event.payload.team, names),
        rotation: event.payload.rotation.join('-'),
      });
    case 'touch': {
      const p = event.payload;
      const vars = {
        team: teamName(p.team, names),
        skill: skillLabel(p.skill, t),
        evaluation: p.evaluation,
        player: player(p.team, p.player, names, t),
        startZone: p.startZone ?? '·',
        endZone: p.endZone ?? '·',
      };
      return p.startZone !== undefined || p.endZone !== undefined
        ? t('describe.touchZones', vars)
        : t('describe.touch', vars);
    }
    case 'point':
      return t('describe.point', {
        team: teamName(event.payload.wonBy, names),
        reason: reasonLabel(event.payload.reason, t).toLowerCase(),
        home: event.payload.homeScore,
        away: event.payload.awayScore,
      });
    case 'substitution':
      return t(
        event.payload.isLibero ? 'describe.substitutionLibero' : 'describe.substitution',
        {
          team: teamName(event.payload.team, names),
          playerIn: event.payload.playerIn,
          playerOut: event.payload.playerOut,
        },
      );
    case 'timeout':
      return t(
        event.payload.kind === 'technical'
          ? 'describe.timeoutTechnical'
          : 'describe.timeoutRegular',
        { team: teamName(event.payload.team, names) },
      );
    case 'sanction':
      return t('describe.sanction', {
        team: teamName(event.payload.team, names),
        card: event.payload.card,
      });
    case 'comment':
      return t('describe.comment', { text: event.payload.text });
    case 'undo':
      return t('describe.undo');
    case 'correction':
      return t('describe.correction');

    // --- Acta electrónica (docs/acta-electronica.md §3.1) ------------------
    case 'officials_set':
      return t('describe.officialsSet', { count: event.payload.officials.length });
    case 'toss': {
      const p = event.payload;
      const vars = { team: teamName(p.winner, names), choice: p.choice };
      return p.scope === 'DECIDER' ? t('describe.tossDecider', vars) : t('describe.toss', vars);
    }
    case 'lineup_approved':
      return t('describe.lineupApproved', {
        team: teamName(event.payload.team, names),
        set: event.payload.setNumber,
      });
    case 'rally_started':
      return t('describe.rallyStarted', {
        team: teamName(event.payload.servingTeam, names),
        server: event.payload.server,
      });
    case 'challenge_requested': {
      const team = event.payload.team;
      // Revisión de oficio (art. 12): no hay equipo que la haya pedido, y
      // decir "challenge de local" sería falso en el acta.
      return team === null
        ? t('describe.challengeOfficial')
        : t('describe.challengeRequested', { team: teamName(team, names) });
    }
    case 'challenge_resolved':
      return t('describe.challengeResolved', {
        outcome: event.payload.outcome,
        action: event.payload.scoreAction,
      });
    case 'replay_bookmark':
      return t('describe.replayBookmark', { label: event.payload.label });
    case 'signature':
      return t('describe.signature', {
        role: event.payload.role,
        name: event.payload.signerName,
      });
    case 'sheet_closed':
      return t('describe.sheetClosed', { outcome: event.payload.outcome });
  }
}
